import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Grid2,
  InputAdornment,
  TextField
} from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useReferenceData } from "../../../hooks";
import { useFormStyles } from "../../../hooks/useFormStyles";
import { getLithologyFormSchema, LithologyForm as LithologyFormType } from "../../../types/forms/capture";
import { CaptureLithology } from "../../../types/model/captureLithology";
import FormAutocomplete from "../../General/FormAutocomplete";

interface LithologyFormProps {
  lithologyData?: CaptureLithology;
  formId: string;
  isEditing: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onSubmit: (values: LithologyFormType) => Promise<void>;
}

export default function LithologyForm({
  lithologyData,
  formId,
  isEditing,
  isSaving,
  isDeleting,
  onSubmit,
}: Readonly<LithologyFormProps>) {
  const { t } = useTranslation();
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getLithologyFormSchema(t);
  const methods = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      startDepth: lithologyData?.startDepth ?? undefined,
      endDepth: lithologyData?.endDepth ?? undefined,
      lithologyId: lithologyData?.lithologyId ?? undefined,
      hydraulicConductivity: lithologyData?.hydraulicConductivity ?? null,
      drillingAdvanceRate: lithologyData?.drillingAdvanceRate ?? null,
      secondaryFeatureId: lithologyData?.secondaryFeatureId ?? null,
      textureId: lithologyData?.textureId ?? null,
      colorId: lithologyData?.colorId ?? null,
      aquiferId: lithologyData?.aquiferId ?? null,
      notes: lithologyData?.notes ?? null
    },
  });

  const { data: lithologies = [] } = useReferenceData("geology", "lithologies");
  const { data: secondaryFeatures = [] } = useReferenceData("geology", "secondary_features");
  const { data: textures = [] } = useReferenceData("geology", "textures");
  const { data: colors = [] } = useReferenceData("geology", "colors");
  const { data: aquiferSystems = [] } = useReferenceData("hydro", "aquifer_systems");

  const handleFormSubmit = async (values: LithologyFormType) => {
    await onSubmit(values);
  };

  return (
    <FormProvider {...methods}>
      <Box component="form" id={formId} onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={6}>
            <TextField
              fullWidth
              label={`${t("captures.capture.lithology.start-depth")} *`}                         
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register(
                "startDepth",
                { valueAsNumber: true }
              )}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>
                  ),
                },
              }}
              error={!!methods.formState.errors.startDepth}
              helperText={methods.formState.errors.startDepth?.message}
            />
          </Grid2>
          <Grid2 size={6}>
            <TextField
              fullWidth
              label={`${t("captures.capture.lithology.end-depth")} *`}                         
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register(
                "endDepth",
                { valueAsNumber: true }
              )}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>
                  ),
                },
              }}
              error={!!methods.formState.errors.endDepth}
              helperText={methods.formState.errors.endDepth?.message}
            />
          </Grid2>
          <Grid2 size={6}>
            <FormAutocomplete
              name="lithologyId" 
              label={`${t("captures.capture.lithology.lithology-type")} *`}
              options={lithologies}
              isEditing={isEditing}
              isSaving={isSaving}
              disabled={false}
              formStyles={formStyles}
            />
          </Grid2>
          <Grid2 size={6}>
            <FormAutocomplete
              name="aquiferId" 
              label={t("captures.capture.lithology.aquifer-system")} 
              options={aquiferSystems}
              isEditing={isEditing}
              isSaving={isSaving}
              disabled={false}
              formStyles={formStyles}
            />
          </Grid2>
          <Grid2 size={6}>
            <TextField
              fullWidth
              label={t("captures.capture.lithology.hydraulic-conductivity")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              type="text"
              inputMode="decimal"
              {...methods.register("hydraulicConductivity")}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">{t("generic.units.m-per-day")}</InputAdornment>
                  ),
                },
              }}
              error={!!methods.formState.errors.hydraulicConductivity}
              helperText={methods.formState.errors.hydraulicConductivity?.message}
            />
          </Grid2>
          <Grid2 size={6}>
            <TextField
              fullWidth
              label={t("captures.capture.lithology.drilling-advance-rate")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              type="text"
              inputMode="decimal"
              {...methods.register("drillingAdvanceRate")}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">{t("generic.units.minutes")}</InputAdornment>,
                },
              }}
              error={!!methods.formState.errors.drillingAdvanceRate}
              helperText={methods.formState.errors.drillingAdvanceRate?.message}
            />
          </Grid2>
          <Grid2 size={4}>
            <FormAutocomplete
              name="secondaryFeatureId" 
              label={t("captures.capture.lithology.secondary-feature")} 
              options={secondaryFeatures}
              isEditing={isEditing}
              isSaving={isSaving}
              disabled={false}
              formStyles={formStyles}
            />
          </Grid2>
          <Grid2 size={4}>
            <FormAutocomplete
              name="textureId" 
              label={t("captures.capture.lithology.texture")} 
              options={textures}
              isEditing={isEditing}
              isSaving={isSaving}
              disabled={false}
              formStyles={formStyles}
            />
          </Grid2>
          <Grid2 size={4}>
            <FormAutocomplete
              name="colorId" 
              label={t("captures.capture.lithology.color")} 
              options={colors}
              isEditing={isEditing}
              isSaving={isSaving}
              disabled={false}
              formStyles={formStyles}
            />
          </Grid2>

          <Grid2 size={{xs: 12 }}>
            <TextField
              fullWidth
              label={t("captures.capture.lithology.notes")}
              placeholder={t("captures.capture.lithology.notes-placeholder")}
              multiline
              minRows={6}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("notes")}
              error={!!methods.formState.errors.notes}
              helperText={methods.formState.errors.notes?.message}
            />
          </Grid2>
        </Grid2>
      </Box>
    </FormProvider>
  );
}